import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import AdminPage from '../../../../components/admin/AdminPage';
import Loader from '../../../../components/admin/Loader';
import NoData from '../../../../components/common/NoData';
import {
  getAllCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  toggleCategoryStatus,
  createSubcategory,
  updateSubcategory,
  deleteSubcategory,
  toggleSubcategoryStatus,
  getSubcategoriesByCategory,
} from '../../../../services/adminService';

const inputClass = 'w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50/50 focus:bg-white focus:ring-2 focus:ring-[#4F6BFF]/20 focus:border-[#4F6BFF] outline-none transition-all text-sm';
const labelClass = 'block text-sm font-medium text-gray-700 mb-1.5';

function CourseCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSubcategory, setEditingSubcategory] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState([]);
  
  const [categoryForm, setCategoryForm] = useState({ category_name: '', category_description: '' });
  const [subcategoryForm, setSubcategoryForm] = useState({ 
    category_id: '', 
    subcategory_name: '', 
    subcategory_description: '' 
  });
  const [formErrors, setFormErrors] = useState({});

  // Fetch all categories with their subcategories
  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await getAllCategories(1, 100);
      if (res?.success && Array.isArray(res?.data?.categories)) {
        // Fetch subcategories for each category
        const categoriesWithSubs = await Promise.all(
          res.data.categories.map(async (category) => {
            try {
              const subRes = await getSubcategoriesByCategory(category.id);
              return {
                ...category,
                subcategories: subRes?.success ? subRes.data?.subcategories || [] : []
              };
            } catch (err) {
              console.error(`Failed to fetch subcategories for category ${category.id}`, err);
              return { ...category, subcategories: [] };
            }
          })
        );
        setCategories(categoriesWithSubs);
      } else {
        setCategories([]);
      }
    } catch (err) {
      console.error('Failed to fetch categories', err);
      toast.error('Could not load categories');
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  // Toggle category expansion
  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  // Category Modal Functions
  const openAddCategory = () => {
    setEditingCategory(null);
    setCategoryForm({ category_name: '', category_description: '' });
    setFormErrors({});
    setShowCategoryModal(true);
  };

  const openEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      category_name: category.category_name || '',
      category_description: category.category_description || '',
    });
    setFormErrors({});
    setShowCategoryModal(true);
  };

  const closeCategoryModal = () => {
    setShowCategoryModal(false);
    setEditingCategory(null);
    setCategoryForm({ category_name: '', category_description: '' });
    setFormErrors({});
  };

  // Subcategory Modal Functions
  const openAddSubcategory = (categoryId = '') => {
    setEditingSubcategory(null);
    setSubcategoryForm({ 
      category_id: categoryId, 
      subcategory_name: '', 
      subcategory_description: '' 
    });
    setFormErrors({});
    setShowSubcategoryModal(true);
  };

  const openEditSubcategory = (subcategory, categoryId) => {
    setEditingSubcategory(subcategory);
    setSubcategoryForm({
      category_id: categoryId || subcategory.category_id || '',
      subcategory_name: subcategory.subcategory_name || '',
      subcategory_description: subcategory.subcategory_description || '',
    });
    setFormErrors({});
    setShowSubcategoryModal(true);
  };

  const closeSubcategoryModal = () => {
    setShowSubcategoryModal(false);
    setEditingSubcategory(null);
    setSubcategoryForm({ category_id: '', subcategory_name: '', subcategory_description: '' });
    setFormErrors({});
  };

  // Validation
  const validateCategory = () => {
    const name = (categoryForm.category_name || '').trim();
    if (!name) {
      setFormErrors({ category_name: 'Category name is required' });
      return false;
    }
    setFormErrors({});
    return true;
  };

  const validateSubcategory = () => {
    const errors = {};
    const name = (subcategoryForm.subcategory_name || '').trim();
    
    if (!subcategoryForm.category_id) {
      errors.category_id = 'Parent category is required';
    }
    if (!name) {
      errors.subcategory_name = 'Subcategory name is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Submit Handlers
  const handleCategorySubmit = async (e) => {
    e.preventDefault();
    if (!validateCategory()) return;
    
    setSubmitting(true);
    try {
      const payload = {
        category_name: categoryForm.category_name.trim(),
        category_description: categoryForm.category_description.trim(),
      };
      
      if (editingCategory) {
        const res = await updateCategory(editingCategory.id, payload);
        if (res?.success) {
          toast.success('Category updated successfully');
          closeCategoryModal();
          fetchCategories();
        } else {
          toast.error(res?.message || 'Failed to update category');
        }
      } else {
        const res = await createCategory(payload);
        if (res?.success) {
          toast.success('Category created successfully');
          closeCategoryModal();
          fetchCategories();
        } else {
          toast.error(res?.message || 'Failed to create category');
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Request failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubcategorySubmit = async (e) => {
    e.preventDefault();
    if (!validateSubcategory()) return;
    
    setSubmitting(true);
    try {
      const payload = {
        category_id: parseInt(subcategoryForm.category_id),
        subcategory_name: subcategoryForm.subcategory_name.trim(),
        subcategory_description: subcategoryForm.subcategory_description.trim(),
      };
      
      if (editingSubcategory) {
        const res = await updateSubcategory(editingSubcategory.id, payload);
        if (res?.success) {
          toast.success('Subcategory updated successfully');
          closeSubcategoryModal();
          fetchCategories();
        } else {
          toast.error(res?.message || 'Failed to update subcategory');
        }
      } else {
        const res = await createSubcategory(payload);
        if (res?.success) {
          toast.success('Subcategory created successfully');
          closeSubcategoryModal();
          fetchCategories();
        } else {
          toast.error(res?.message || 'Failed to create subcategory');
        }
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Request failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Delete Functions
  const openDeleteModal = (item, type) => {
    setDeleteTarget({ ...item, type });
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    
    setSubmitting(true);
    try {
      const res = deleteTarget.type === 'category'
        ? await deleteCategory(deleteTarget.id)
        : await deleteSubcategory(deleteTarget.id);
        
      if (res?.success) {
        toast.success(`${deleteTarget.type === 'category' ? 'Category' : 'Subcategory'} deleted successfully`);
        closeDeleteModal();
        fetchCategories();
      } else {
        toast.error(res?.message || 'Failed to delete');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Delete failed');
    } finally {
      setSubmitting(false);
    }
  };

  // Toggle Status
  const handleToggleStatus = async (id, type) => {
    try {
      const res = type === 'category'
        ? await toggleCategoryStatus(id)
        : await toggleSubcategoryStatus(id);
        
      if (res?.success) {
        toast.success(`${type === 'category' ? 'Category' : 'Subcategory'} status updated`);
        fetchCategories();
      } else {
        toast.error(res?.message || 'Failed to update status');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Status update failed');
    }
  };

  return (
    <AdminPage
      title="Manage Categories"
      description="Create and manage categories and subcategories for organizing your courses."
      actions={
        <>
          <Link
            to="/admin/courses"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium text-sm hover:bg-gray-50 no-underline"
          >
            <Icon icon="fluent:arrow-left-24-regular" width="18" height="18" />
            Back to Courses
          </Link>
          <button
            type="button"
            onClick={openAddCategory}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#4F6BFF] hover:bg-[#4358d4] text-white font-medium text-sm"
          >
            <Icon icon="fluent:add-24-regular" width="18" height="18" />
            Add Category
          </button>
          <button
            type="button"
            onClick={() => openAddSubcategory()}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#4F6BFF] text-[#4F6BFF] hover:bg-[#4F6BFF]/10 font-medium text-sm"
          >
            <Icon icon="fluent:add-24-regular" width="18" height="18" />
            Add Subcategory
          </button>
        </>
      }
    >
      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center min-h-[320px]">
            <Loader />
          </div>
        ) : categories.length === 0 ? (
          <div className="p-8">
            <NoData message="No categories yet. Create one to get started." />
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {categories.map((category) => (
              <div key={category.id} className="bg-white">
                {/* Category Row */}
                <div className="flex items-center justify-between p-4 hover:bg-gray-50/50">
                  <div className="flex items-center gap-3 flex-1">
                    <button
                      onClick={() => toggleCategory(category.id)}
                      className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <Icon
                        icon={expandedCategories.includes(category.id) ? "fluent:chevron-down-24-regular" : "fluent:chevron-right-24-regular"}
                        width="20"
                        height="20"
                        className="text-gray-600"
                      />
                    </button>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{category.category_name}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          category.is_active 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-gray-100 text-gray-600'
                        }`}>
                          {category.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      {category.category_description && (
                        <p className="text-sm text-gray-600 mt-1">{category.category_description}</p>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        {category.subcategories?.length || 0} subcategories
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => openAddSubcategory(category.id)}
                      className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100"
                      title="Add Subcategory"
                    >
                      <Icon icon="fluent:add-24-regular" width="18" height="18" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleToggleStatus(category.id, 'category')}
                      className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100"
                      title={category.is_active ? 'Deactivate' : 'Activate'}
                    >
                      <Icon 
                        icon={category.is_active ? "fluent:eye-off-24-regular" : "fluent:eye-24-regular"} 
                        width="18" 
                        height="18" 
                      />
                    </button>
                    <button
                      type="button"
                      onClick={() => openEditCategory(category)}
                      className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100"
                      title="Edit"
                    >
                      <Icon icon="fluent:edit-24-regular" width="18" height="18" />
                    </button>
                    <button
                      type="button"
                      onClick={() => openDeleteModal(category, 'category')}
                      className="p-2 rounded-lg border border-gray-200 text-red-600 hover:bg-red-50"
                      title="Delete"
                    >
                      <Icon icon="fluent:delete-24-regular" width="18" height="18" />
                    </button>
                  </div>
                </div>

                {/* Subcategories */}
                {expandedCategories.includes(category.id) && (
                  <div className="bg-gray-50/50 border-t border-gray-100">
                    {category.subcategories && category.subcategories.length > 0 ? (
                      <div className="divide-y divide-gray-100">
                        {category.subcategories.map((subcategory) => (
                          <div key={subcategory.id} className="flex items-center justify-between p-4 pl-12 hover:bg-gray-100/50">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <Icon icon="fluent:arrow-turn-down-right-24-regular" width="16" height="16" className="text-gray-400" />
                                <h4 className="font-medium text-gray-800">{subcategory.subcategory_name}</h4>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  subcategory.is_active 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-gray-100 text-gray-600'
                                }`}>
                                  {subcategory.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                              {subcategory.subcategory_description && (
                                <p className="text-sm text-gray-600 mt-1 ml-6">{subcategory.subcategory_description}</p>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => handleToggleStatus(subcategory.id, 'subcategory')}
                                className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100"
                                title={subcategory.is_active ? 'Deactivate' : 'Activate'}
                              >
                                <Icon 
                                  icon={subcategory.is_active ? "fluent:eye-off-24-regular" : "fluent:eye-24-regular"} 
                                  width="18" 
                                  height="18" 
                                />
                              </button>
                              <button
                                type="button"
                                onClick={() => openEditSubcategory(subcategory, category.id)}
                                className="p-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100"
                                title="Edit"
                              >
                                <Icon icon="fluent:edit-24-regular" width="18" height="18" />
                              </button>
                              <button
                                type="button"
                                onClick={() => openDeleteModal(subcategory, 'subcategory')}
                                className="p-2 rounded-lg border border-gray-200 text-red-600 hover:bg-red-50"
                                title="Delete"
                              >
                                <Icon icon="fluent:delete-24-regular" width="18" height="18" />
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-4 pl-12 text-sm text-gray-500">
                        No subcategories yet. Click + to add one.
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Category Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
            <div className="px-5 py-4 border-b border-gray-100 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingCategory ? 'Edit Category' : 'Add Category'}
              </h3>
              <button type="button" onClick={closeCategoryModal} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
                <Icon icon="mdi:close" width="20" height="20" />
              </button>
            </div>
            <form onSubmit={handleCategorySubmit} className="p-5 space-y-4">
              <div>
                <label className={labelClass}>Name *</label>
                <input
                  type="text"
                  className={formErrors.category_name ? `${inputClass} border-red-500` : inputClass}
                  value={categoryForm.category_name}
                  onChange={(e) => setCategoryForm((p) => ({ ...p, category_name: e.target.value }))}
                  placeholder="e.g. Technology"
                  maxLength={120}
                />
                {formErrors.category_name && <p className="text-red-500 text-xs mt-1">{formErrors.category_name}</p>}
              </div>
              <div>
                <label className={labelClass}>Description</label>
                <textarea
                  className={inputClass}
                  value={categoryForm.category_description}
                  onChange={(e) => setCategoryForm((p) => ({ ...p, category_description: e.target.value }))}
                  placeholder="Brief description of the category"
                  rows={3}
                  maxLength={500}
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeCategoryModal}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-[#4F6BFF] hover:bg-[#4358d4] text-white font-medium text-sm disabled:opacity-50"
                >
                  {submitting ? 'Saving…' : editingCategory ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Subcategory Modal */}
      {showSubcategoryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
            <div className="px-5 py-4 border-b border-gray-100 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingSubcategory ? 'Edit Subcategory' : 'Add Subcategory'}
              </h3>
              <button type="button" onClick={closeSubcategoryModal} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
                <Icon icon="mdi:close" width="20" height="20" />
              </button>
            </div>
            <form onSubmit={handleSubcategorySubmit} className="p-5 space-y-4">
              <div>
                <label className={labelClass}>Parent Category *</label>
                <select
                  className={formErrors.category_id ? `${inputClass} border-red-500` : inputClass}
                  value={subcategoryForm.category_id}
                  onChange={(e) => setSubcategoryForm((p) => ({ ...p, category_id: e.target.value }))}
                >
                  <option value="">Select a category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.category_name}
                    </option>
                  ))}
                </select>
                {formErrors.category_id && <p className="text-red-500 text-xs mt-1">{formErrors.category_id}</p>}
              </div>
              <div>
                <label className={labelClass}>Name *</label>
                <input
                  type="text"
                  className={formErrors.subcategory_name ? `${inputClass} border-red-500` : inputClass}
                  value={subcategoryForm.subcategory_name}
                  onChange={(e) => setSubcategoryForm((p) => ({ ...p, subcategory_name: e.target.value }))}
                  placeholder="e.g. Web Development"
                  maxLength={120}
                />
                {formErrors.subcategory_name && <p className="text-red-500 text-xs mt-1">{formErrors.subcategory_name}</p>}
              </div>
              <div>
                <label className={labelClass}>Description</label>
                <textarea
                  className={inputClass}
                  value={subcategoryForm.subcategory_description}
                  onChange={(e) => setSubcategoryForm((p) => ({ ...p, subcategory_description: e.target.value }))}
                  placeholder="Brief description of the subcategory"
                  rows={3}
                  maxLength={500}
                />
              </div>
              <div className="flex gap-2 pt-2">
                <button
                  type="button"
                  onClick={closeSubcategoryModal}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-[#4F6BFF] hover:bg-[#4358d4] text-white font-medium text-sm disabled:opacity-50"
                >
                  {submitting ? 'Saving…' : editingSubcategory ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-sm w-full p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <Icon icon="fluent:warning-24-regular" width="24" height="24" className="text-red-600" />
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Confirm Delete</h4>
                <p className="text-sm text-gray-600">This action cannot be undone</p>
              </div>
            </div>
            <p className="text-gray-700 mb-4">
              Are you sure you want to delete <strong>{deleteTarget.category_name || deleteTarget.subcategory_name}</strong>?
              {deleteTarget.type === 'category' && deleteTarget.subcategories?.length > 0 && (
                <span className="block text-red-600 text-sm mt-2">
                  Warning: This category has {deleteTarget.subcategories.length} subcategories.
                </span>
              )}
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={closeDeleteModal}
                className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium text-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={submitting}
                className="flex-1 px-4 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white font-medium text-sm disabled:opacity-50"
              >
                {submitting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminPage>
  );
}

export default CourseCategories;
